'use client';

import { useEffect, useRef, useState } from 'react';
import { 
  Sparkles, 
  BookOpen, 
  GitBranch, 
  Zap, 
  CheckCircle2, 
  XCircle,
  Target,
  Users,
  MessageSquare,
  BarChart3,
  Share2,
  Map,
  Film,
  FileText
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';

// Artifact type for different tabs
export type ArtifactType = 
  | 'goals' 
  | 'interviews' 
  | 'competitors' 
  | 'personas' 
  | 'use-cases' 
  | 'journeys' 
  | 'service-maps' 
  | 'storyboards'
  | 'overview';

interface AIGenerationProgressProps {
  isGenerating: boolean;
  onCancel?: () => void;
  error?: string | null;
  onRetry?: () => void;
  /** The type of artifact being generated */
  artifactType?: ArtifactType;
  /** Optional: Templates being referenced during generation */
  referencedTemplates?: string[];
}

type AnimationStage = 'idle' | 'processing' | 'checking' | 'referencing' | 'aligning' | 'finalizing' | 'complete';

interface StageConfig {
  icon: React.ReactNode;
  title: string;
  description: string;
}

// Default stage configurations
const DEFAULT_STAGE_CONFIG: Record<string, StageConfig> = {
  processing: {
    icon: <Sparkles className="w-8 h-8 text-indigo-600 animate-pulse" />,
    title: 'Processing your request...',
    description: 'Initializing AI generation'
  },
  checking: {
    icon: <BookOpen className="w-8 h-8 text-indigo-600" />,
    title: 'Checking project knowledge...',
    description: 'Retrieving relevant information'
  },
  referencing: {
    icon: <GitBranch className="w-8 h-8 text-indigo-600" />,
    title: 'Referencing related templates...',
    description: 'Analyzing template relationships'
  },
  aligning: {
    icon: <Zap className="w-8 h-8 text-indigo-600" />,
    title: 'Aligning insights and structure...',
    description: 'Generating structured output'
  },
  finalizing: {
    icon: <CheckCircle2 className="w-8 h-8 text-indigo-600" />,
    title: 'Finalizing...',
    description: 'Preparing your content'
  }
};

// Artifact-specific configurations
const ARTIFACT_CONFIG: Record<ArtifactType, {
  stages: Record<string, { title: string; description: string }>;
  templates: string[];
  icon: React.ReactNode;
}> = {
  goals: {
    stages: {
      processing: { title: 'Processing your request...', description: 'Initializing AI generation' },
      checking: { title: 'Checking project knowledge...', description: 'Analyzing project context' },
      referencing: { title: 'Referencing related templates...', description: 'Analyzing template relationships' },
      aligning: { title: 'Aligning insights and structure...', description: 'Generating structured output' },
      finalizing: { title: 'Finalizing goals...', description: 'Preparing your goals' }
    },
    templates: ['Goals Template', 'OKR Framework', 'Success Metrics'],
    icon: <Target className="w-8 h-8 text-indigo-600" />
  },
  interviews: {
    stages: {
      processing: { title: 'Processing your request...', description: 'Initializing AI generation' },
      checking: { title: 'Checking project knowledge...', description: 'Analyzing stakeholder context' },
      referencing: { title: 'Referencing related templates...', description: 'Analyzing template relationships' },
      aligning: { title: 'Structuring interview insights...', description: 'Organizing key findings' },
      finalizing: { title: 'Finalizing interviews...', description: 'Preparing interview summaries' }
    },
    templates: ['Persona Template', 'Research Summary', 'Stakeholder Map'],
    icon: <MessageSquare className="w-8 h-8 text-indigo-600" />
  },
  competitors: {
    stages: {
      processing: { title: 'Processing your request...', description: 'Initializing AI generation' },
      checking: { title: 'Checking market knowledge...', description: 'Analyzing competitive landscape' },
      referencing: { title: 'Referencing analysis templates...', description: 'Analyzing template relationships' },
      aligning: { title: 'Structuring competitor analysis...', description: 'Organizing market insights' },
      finalizing: { title: 'Finalizing analysis...', description: 'Preparing competitor profiles' }
    },
    templates: ['Competitor Analysis Template', 'Market Research', 'SWOT Framework'],
    icon: <BarChart3 className="w-8 h-8 text-indigo-600" />
  },
  personas: {
    stages: {
      processing: { title: 'Processing your request...', description: 'Initializing AI generation' },
      checking: { title: 'Checking user research...', description: 'Analyzing user context' },
      referencing: { title: 'Referencing related templates...', description: 'Analyzing template relationships' },
      aligning: { title: 'Building persona profiles...', description: 'Structuring user characteristics' },
      finalizing: { title: 'Finalizing personas...', description: 'Preparing user personas' }
    },
    templates: ['Persona Template', 'User Research Summary', 'Demographics Framework'],
    icon: <Users className="w-8 h-8 text-indigo-600" />
  },
  'use-cases': {
    stages: {
      processing: { title: 'Processing your request...', description: 'Initializing AI generation' },
      checking: { title: 'Checking system requirements...', description: 'Analyzing functional context' },
      referencing: { title: 'Referencing related templates...', description: 'Analyzing template relationships' },
      aligning: { title: 'Structuring use case flows...', description: 'Organizing actor interactions' },
      finalizing: { title: 'Finalizing use cases...', description: 'Preparing use case documentation' }
    },
    templates: ['Use Case Template', 'Actor Mapping', 'Flow Diagrams'],
    icon: <Target className="w-8 h-8 text-indigo-600" />
  },
  journeys: {
    stages: {
      processing: { title: 'Processing your request...', description: 'Initializing AI generation' },
      checking: { title: 'Checking user context...', description: 'Analyzing journey touchpoints' },
      referencing: { title: 'Referencing related templates...', description: 'Analyzing template relationships' },
      aligning: { title: 'Mapping journey stages...', description: 'Structuring user experience' },
      finalizing: { title: 'Finalizing journeys...', description: 'Preparing journey maps' }
    },
    templates: ['User Journey Map', 'Touchpoint Analysis', 'Emotion Mapping'],
    icon: <Share2 className="w-8 h-8 text-indigo-600" />
  },
  'service-maps': {
    stages: {
      processing: { title: 'Processing your request...', description: 'Initializing AI generation' },
      checking: { title: 'Checking service context...', description: 'Analyzing service architecture' },
      referencing: { title: 'Referencing related templates...', description: 'Analyzing template relationships' },
      aligning: { title: 'Mapping service layers...', description: 'Structuring frontstage/backstage' },
      finalizing: { title: 'Finalizing service map...', description: 'Preparing service blueprint' }
    },
    templates: ['Service Blueprint', 'System Integration', 'Process Flow'],
    icon: <Map className="w-8 h-8 text-indigo-600" />
  },
  storyboards: {
    stages: {
      processing: { title: 'Processing your request...', description: 'Initializing AI generation' },
      checking: { title: 'Checking scenario context...', description: 'Analyzing narrative elements' },
      referencing: { title: 'Referencing related templates...', description: 'Analyzing template relationships' },
      aligning: { title: 'Building storyboard frames...', description: 'Structuring visual narrative' },
      finalizing: { title: 'Finalizing storyboard...', description: 'Preparing storyboard frames' }
    },
    templates: ['Storyboard Template', 'Scenario Framework', 'Visual Narrative'],
    icon: <Film className="w-8 h-8 text-indigo-600" />
  },
  overview: {
    stages: {
      processing: { title: 'Processing your request...', description: 'Initializing AI generation' },
      checking: { title: 'Checking project context...', description: 'Analyzing project details' },
      referencing: { title: 'Referencing related templates...', description: 'Analyzing template relationships' },
      aligning: { title: 'Structuring project overview...', description: 'Organizing key information' },
      finalizing: { title: 'Finalizing overview...', description: 'Preparing project summary' }
    },
    templates: ['Project Brief', 'Executive Summary', 'Scope Document'],
    icon: <FileText className="w-8 h-8 text-indigo-600" />
  }
};

const STEPS = [
  { stage: 'checking', label: 'Checking Knowledge' },
  { stage: 'referencing', label: 'Referencing Templates' },
  { stage: 'aligning', label: 'Aligning Content' },
  { stage: 'finalizing', label: 'Finalizing' }
];

// Stage progression order for comparison
const STAGE_ORDER = ['idle', 'processing', 'checking', 'referencing', 'aligning', 'finalizing', 'complete'];

export function AIGenerationProgress({
  isGenerating,
  onCancel,
  error,
  onRetry,
  artifactType = 'goals',
  referencedTemplates,
}: AIGenerationProgressProps) {
  const [animationStage, setAnimationStage] = useState<AnimationStage>('idle');
  const [visibleTemplates, setVisibleTemplates] = useState<string[]>([]);
  const [isDismissed, setIsDismissed] = useState(false);
  const wasGeneratingRef = useRef(false);

  // Get artifact-specific config
  const artifactConfig = ARTIFACT_CONFIG[artifactType] || ARTIFACT_CONFIG.goals;
  const templates = referencedTemplates || artifactConfig.templates;

  useEffect(() => {
    if (isGenerating && !wasGeneratingRef.current) {
      setIsDismissed(false);
    }
    wasGeneratingRef.current = isGenerating;
  }, [isGenerating]);

  useEffect(() => {
    if (!isGenerating) {
      setAnimationStage('idle');
      setVisibleTemplates([]);
      return;
    }

    const stages: { stage: AnimationStage; duration: number }[] = [
      { stage: 'processing', duration: 800 },
      { stage: 'checking', duration: 1500 },
      { stage: 'referencing', duration: 1800 },
      { stage: 'aligning', duration: 1500 },
      { stage: 'finalizing', duration: 800 },
    ];

    let currentStageIndex = 0;
    const timers: NodeJS.Timeout[] = [];

    const advanceStage = () => {
      if (currentStageIndex < stages.length) {
        const { stage, duration } = stages[currentStageIndex];
        setAnimationStage(stage);
        
        // Show template pills during referencing stage
        if (stage === 'referencing') {
          setVisibleTemplates(templates.slice(0, 3));
        }
        
        currentStageIndex++;
        if (duration > 0) {
          const timer = setTimeout(advanceStage, duration);
          timers.push(timer);
        }
      }
    };

    advanceStage();

    return () => {
      timers.forEach(t => clearTimeout(t));
    };
  }, [isGenerating, templates]);

  if ((!isGenerating && !error) || (isDismissed && isGenerating && !error)) return null;

  // Error state
  if (error) {
    return (
      <Dialog open={true} onOpenChange={() => onCancel?.()}>
        <DialogContent className="max-w-md p-0 overflow-hidden rounded-xl border-0">
          <DialogTitle className="sr-only">Generation Failed</DialogTitle>
          <DialogDescription className="sr-only">AI content generation encountered an error</DialogDescription>
          <div className="bg-white rounded-xl p-12 min-h-[400px] flex items-center justify-center">
            <div className="max-w-md w-full text-center">
              <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-6">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Generation Failed</h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <div className="flex items-center justify-center gap-3">
                {onRetry && (
                  <button
                    onClick={onRetry}
                    className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Try Again
                  </button>
                )}
                {onCancel && (
                  <button
                    onClick={onCancel}
                    className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Get stage-specific config
  const stageConfig = artifactConfig.stages[animationStage];
  if (!stageConfig) return null;

  // Get appropriate icon based on stage
  const getStageIcon = () => {
    switch (animationStage) {
      case 'processing':
        return <Sparkles className="w-8 h-8 text-indigo-600 animate-pulse" />;
      case 'checking':
        return <BookOpen className="w-8 h-8 text-indigo-600" />;
      case 'referencing':
        return <GitBranch className="w-8 h-8 text-indigo-600" />;
      case 'aligning':
        return <Zap className="w-8 h-8 text-indigo-600" />;
      case 'finalizing':
        return <CheckCircle2 className="w-8 h-8 text-indigo-600" />;
      default:
        return artifactConfig.icon;
    }
  };

  // Check if a step is complete or current
  const isStepComplete = (stepStage: string) => {
    return STAGE_ORDER.indexOf(animationStage) > STAGE_ORDER.indexOf(stepStage);
  };
  
  const isStepCurrent = (stepStage: string) => {
    return animationStage === stepStage;
  };

  return (
    <Dialog
      open={isGenerating && !isDismissed}
      onOpenChange={(open) => {
        if (!open) {
          setIsDismissed(true);
          onCancel?.();
        }
      }}
    >
      <DialogContent className="max-w-md p-0 overflow-hidden rounded-xl border-0">
        <DialogTitle className="sr-only">AI Generation in Progress</DialogTitle>
        <DialogDescription className="sr-only">Generating {artifactType} content using AI</DialogDescription>
        <div className="bg-white rounded-xl p-12 min-h-[500px] flex items-center justify-center">
          <div className="max-w-md w-full">
            {/* Main Animation Container */}
            <div className="text-center">
              {/* Animated Icon with Halo */}
              <div className="relative inline-flex items-center justify-center mb-6">
                {/* Outer halo - pinging */}
                <div 
                  className="absolute inset-0 bg-indigo-200 rounded-full opacity-20 animate-ping" 
                  style={{ animationDuration: '2s' }}
                />
                {/* Middle halo - pulsing */}
                <div 
                  className="absolute inset-2 bg-indigo-300 rounded-full opacity-30 animate-pulse" 
                  style={{ animationDuration: '1.5s' }}
                />
                {/* Icon container */}
                <div className="relative w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center">
                  {getStageIcon()}
                </div>
              </div>

              {/* Stage Title */}
              <h3 className="text-lg font-medium text-gray-900 mb-2">{stageConfig.title}</h3>
              <p className="text-gray-600 mb-6">{stageConfig.description}</p>

              {/* Thinking Dots - Bouncing */}
              <div className="flex items-center justify-center gap-2 mb-8">
                <div 
                  className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" 
                  style={{ animationDelay: '0ms' }}
                />
                <div 
                  className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" 
                  style={{ animationDelay: '150ms' }}
                />
                <div 
                  className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" 
                  style={{ animationDelay: '300ms' }}
                />
              </div>

              {/* Related Templates Pills (only during referencing stage) */}
              {animationStage === 'referencing' && visibleTemplates.length > 0 && (
                <div className="space-y-3 mb-8">
                  {visibleTemplates.map((template, index) => (
                    <div
                      key={template}
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-200 rounded-full text-indigo-700 text-sm animate-slide-in"
                      style={{ 
                        animationDelay: `${index * 150}ms`, 
                        animationFillMode: 'backwards' 
                      }}
                    >
                      <GitBranch className="w-3 h-3" />
                      <span>{template}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Progress Steps */}
              <div className="space-y-2">
                {STEPS.map((step) => {
                  const isComplete = isStepComplete(step.stage);
                  const isCurrent = isStepCurrent(step.stage);
                  
                  return (
                    <div 
                      key={step.stage}
                      className={cn(
                        "flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-300",
                        isCurrent && "bg-indigo-50",
                        isComplete && "bg-green-50",
                        !isCurrent && !isComplete && "bg-gray-50"
                      )}
                    >
                      {/* Step Icon */}
                      {isComplete ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : isCurrent ? (
                        <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <div className="w-5 h-5 border-2 border-gray-300 rounded-full" />
                      )}
                      
                      {/* Step Label */}
                      <span className={cn(
                        "text-sm font-medium",
                        isComplete && "text-green-700",
                        isCurrent && "text-indigo-700",
                        !isCurrent && !isComplete && "text-gray-400"
                      )}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>

              {onCancel && (
                <div className="mt-6 text-center">
                  <button
                    onClick={() => {
                      setIsDismissed(true);
                      onCancel();
                    }}
                    className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Custom Animations */}
          <style>{`
            @keyframes slide-in {
              from {
                opacity: 0;
                transform: translateX(-20px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }

            .animate-slide-in {
              animation: slide-in 0.4s ease-out;
            }
          `}</style>
        </div>
      </DialogContent>
    </Dialog>
  );
}
